// Utility functions for color handling

/**
 * Named CSS colors that we support for sticky notes
 */
export const NAMED_COLORS = [
  'coral', 'salmon', 'tomato', 'crimson', 'firebrick',
  'lavender', 'orchid', 'plum', 'violet', 'purple', 'indigo',
  'skyblue', 'steelblue', 'teal', 'cyan', 'turquoise', 'aquamarine',
  'mint', 'lime', 'olive', 'chartreuse', 'forestgreen',
  'peach', 'gold', 'khaki', 'tan', 'sienna', 'chocolate',
  'ivory', 'linen', 'snow', 'mistyrose', 'seashell',
  'hotpink', 'deeppink', 'fuchsia', 'magenta', 'pink',
  'navy', 'royalblue', 'dodgerblue', 'cornflowerblue',
  'orange', 'darkorange', 'orangered',
  'red', 'green', 'blue', 'yellow', 'white', 'black',
  'gray', 'grey', 'silver', 'maroon',
];

/**
 * Detect a color in text - matches hex codes (#RGB, #RRGGBB) and named CSS colors
 */
export function detectColorInText(text: string): { color: string | null; cleanedText: string } {
  // Check for hex code first
  const hexMatch = text.match(/#([0-9a-fA-F]{3}){1,2}\b/);
  
  if (hexMatch) {
    const detectedColor = hexMatch[0];
    const cleanedText = text.replace(hexMatch[0], '').trim();
    return { color: detectedColor, cleanedText };
  }
  
  // Check for named colors
  const words = text.toLowerCase().split(/\s+/);
  const namedMatch = words.find(w => NAMED_COLORS.includes(w));
  
  if (namedMatch) {
    // Remove only the first occurrence of the color word
    const cleanedText = text.replace(new RegExp(`\\b${namedMatch}\\b`, 'i'), '').trim();
    return { color: namedMatch, cleanedText };
  }
  
  return { color: null, cleanedText: text };
}

/**
 * Get a contrasting text color (black or white) for a given background color
 * Works with both hex codes and CSS color names
 */
export function getContrastColor(bgColor: string): string {
  // For named colors and hex, create a temporary element to resolve the color
  const el = document.createElement('div');
  el.style.color = bgColor;
  document.body.appendChild(el);
  const computed = getComputedStyle(el).color;
  document.body.removeChild(el);

  // Parse RGB from computed style
  const match = computed.match(/\d+/g);
  if (!match) return '#1a1a1a';

  const [r, g, b] = match.map(Number);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1a1a1a' : '#ffffff';
}

/**
 * Get sticky color - supports custom colors and preset names
 */
export function getStickyColorValue(color?: string, customColor?: string): string {
  if (customColor) return customColor;
  
  const colors: Record<string, string> = {
    yellow: '#F3E7B3',
    blue: '#D5E3F0',
    pink: '#F0D9D6',
    green: '#D9E4D2',
    purple: '#EDE9FE',
    orange: '#FFEDD5',
    gray: '#E4E2DC',
  };
  
  return colors[color || 'yellow'] || colors.yellow;
}
